class EOIFormAnalytics {

  constructor(doc, livewire) {
    // Common Tracking vars
    this.formName = 'BuyerEOIForm';

    // Helper vars
    this.doc = doc;
    this.pagePath = window.location.pathname;
    this.dataLayer = window.dataLayer;

    this.MDCTypeMap = {
      MDCSelect: 'select',
      MDCSwitch: 'switch'
    };
    
    // State vars
    this.formHasBeenSeen = false;
    
    // Init required functions
    this.formLoaded();
    this.manageOfferListener();
    this.raqFormListener();
  }

  getFormElement() {
    if (!this.formElement) {
      this.formElement = this.doc.querySelector('.m-offerForm form');
    }
    return this.formElement;
  }

  formOnPage() {
    this.dataLayerEvent('formOnPage', {
      action: 'loaded'
    });
  }

  formLoaded() {
    if (window.OtOData.offerFormLoaded) {
      this.formLoadedCallback();
    }
    else {
      window.addEventListener('oto.offerFormLoaded', this.formLoadedCallback.bind(this));
    }
  }

  formLoadedCallback() {
    // Clear out any memory of previous EOI forms
    this.formElement = null;
    this.dataLayerEvent('formOnPage', {
      action: 'loaded'
    });
    // Setup tracking for various form interactions / events once loaded
    this.formVisibleListener();
    this.formInteractedListener();
    this.addPersonListener();
    this.formFieldListeners();
    this.submittedListener();
    this.ctaListeners();
  }

  formVisibleListener() {
    const $this = this;
    const intersectionOpts = {
      threshold: 0.1
    }
    let formObserver = new IntersectionObserver(function(entries) {
      let isIntersecting = entries.some(function(entry){ return entry.isIntersecting });
      if (isIntersecting) {
        $this.dataLayerEvent('formScrolledIntoView', {action: 'viewable'});
        formObserver.disconnect();
      }
    }, intersectionOpts);
    let formTarget = this.doc.querySelector('.m-offerForm');
    formObserver.observe(formTarget);
  }

  formInteractedListener() {
    const $this = this;
    let formEl = this.getFormElement();
    formEl.addEventListener('focusin', function formInteracted(e) {
      let field = e.target;
      // Something causes MDC dialog popups to gain focus
      if (!field.classList.contains('mdc-dialog__close-icon')) {
        const name = $this.getFieldNameOrLabelText(field);
        $this.dataLayerEvent('formInteracted', {
          category: $this.formName + ' - Form Interacted',
          action: $this.formName + ' - ' + name,
        });
        // Stop listening after the first focus
        formEl.removeEventListener('focusin', formInteracted);
      }
    });
  }

  addPersonListener() {
    const $this = this;
    const form = this.doc.querySelector('.m-offerForm');
    const button = form.querySelector('#add-contact-button');
    button.addEventListener('click', function() {
      const contactEls = form.querySelectorAll('.contact');
      const enabledContacts = Array.from(contactEls).filter(function(el) {
        return el.dataset.hidden != 1;
      });
      const buyerCount = enabledContacts.length;
      $this.dataLayerEvent('addbuyer', {
        action: buyerCount
      });
    });
  }

  manageOfferListener() {
    const $this = this;
    window.addEventListener('oto.manageOffer', function(e) {
      const offer = e.detail.offer;
      $this.dataLayerEvent('manageOffer', {
        category: 'Manage Offer',
        action: 'Manage Offer ' + offer.hash + ' - $' + offer.offer
      });
    });
  }

  formFieldListeners() {
    const $this = this;
    let formEl = this.getFormElement();
    // Wait for material components in the form to be initialized
    // This can also happen when each field has a value entered, as the form is rebuilt by livewire
    if (formEl.querySelector('.mdc-form-field').dataset.mdcAutoInitState == 'initialized') {
      this.MDCFormListeners();
    }
    else {
      this.doc.addEventListener('MDCAutoInit:End', this.MDCFormListeners.bind(this));
    }
  }

  MDCFormListeners() {
    const $this = this;
    let formEl = this.getFormElement();
    let formFields = formEl.querySelectorAll('.mdc-form-field');
    formFields.forEach(function(field) {
      if (!field.MDCFormField || field.otoFormAnalyticsRegistered) {
        return;
      }
      // Handle 'switch' & 'checkbox' fields seperately
      let switchEl = field.querySelector('.mdc-switch');
      if (switchEl) {
        switchEl.MDCSwitch.nativeControl_.addEventListener('change', function(e) {
          $this.fieldCompleted(e.target);
        });
        return;
      }
      let checkboxEl = field.querySelector('.mdc-checkbox');
      if (checkboxEl) {
        checkboxEl.MDCCheckbox.nativeControl_.addEventListener('change', function(e) {
          $this.fieldCompleted(e.target);
        });
      }
      // When each field is 'focused'
      field.MDCFormField.foundation.adapter.registerInteractionHandler('focusin', function(e) {
        if (!$this.isMDCFocusTarget(e.target)) {
          return;
        }
        // Determine the field info
        let info = $this.getMDCFieldInfo(e.target);
        $this.dataLayerEvent('fieldStarted', {
          category: $this.formName + ' | Fields',
          action: info.name + ' | ' + info.type,
          label: 'start',
        });
      });
      const focusOutCallback = function(e) {
        if (!$this.isMDCFocusTarget(e.target)) {
          return;
        }
        $this.fieldCompleted(e.target);
        let info = $this.getMDCFieldInfo(e.target);
        // Check validity of field, and if there is an error
        let MDCElement = e.target.closest('[data-mdc-auto-init]');
        let MDCControl = MDCElement[MDCElement.dataset.mdcAutoInit];
        if (!MDCControl.valid) {
          let helperTextEl = field.querySelector('.mdc-text-field-helper-text');
          $this.dataLayerEvent('fieldError', {
            category: $this.formName + ' | Field Error',
            action: info.name + ' | ' + info.type,
            label: helperTextEl.innerText,
          });
        }
      }
      let selectEl = field.querySelector('.mdc-select');
      // Select fields need special handling for focusout/change
      if (!selectEl) {
        // When each field is 'blur'ed (focusout)
        field.MDCFormField.foundation.adapter.registerInteractionHandler('focusout', focusOutCallback);
      }
      else {
        const handleMenuClosed = selectEl.MDCSelect.foundation.handleMenuClosed;
        selectEl.MDCSelect.foundation.handleMenuClosed = function () {
          handleMenuClosed.apply(this);
          focusOutCallback.apply(this, [{target: selectEl}]);
        };
      }
      // Set a flag to prevent duplicate registering of these events when livewire updates
      field.otoFormAnalyticsRegistered = true;
    });
  }

  isMDCFocusTarget(element) {
    // Certain focus events we want to ignore
    if (element.classList.contains('mdc-list-item')) {
      return false;
    }
    return true;
  }

  getMDCFieldInfo(element) {
    let type = element.type;
    let name = element.name;
    let value = '';
    if (!type) {
      let MDCControl = null;
      if (element.dataset.mdcAutoInit) {
        MDCControl = element[element.dataset.mdcAutoInit];
      }
      else {
        let parent = element.parentNode;
        MDCControl = parent[parent.dataset.mdcAutoInit];
      }
      if (MDCControl) {
        type = this.MDCTypeMap[MDCControl.constructor.name];
        input = MDCControl.hiddenInput;
        name = MDCControl.hiddenInput.name;
        if (MDCControl.value) {
          value = MDCControl.value;
        }
      }
    }
    // Determine the value entered
    if (!value) {
      if ('value' in element) {
        input = element;
      }
      if (input.value !== '' && 'pii' in input.dataset) {
        value = 'PII';
      }
      else {
        value = input.value;
      }
    }
    return {
      type: type,
      name: name,
      value: value
    };
  }

  fieldCompleted(target) {
    let info = this.getMDCFieldInfo(target);
    // As well as the completed event, we also want to send the value
    if (info.value) {
      this.dataLayerEvent('formFields', {
        category: this.formName + ' - Form Fields',
        action: this.formName + ' - ' + info.name,
        label: info.value,
      });
    }
    this.dataLayerEvent('fieldCompleted', {
      category: this.formName + ' | Fields',
      action: info.name + ' | ' + info.type,
      label: 'complete',
    });
  }

  submittedListener() {
    const $this = this;
    window.addEventListener('OfferSubmitted', function(e) {
      $this.dataLayerEvent('submitOffer', {
        category: 'Offer Submitted',
        action: $this.formName,
        offerInfo: 'Buyer: ' + e.detail.buyerHash + ', Offer: ' + e.detail.offerHash + ' - ' + e.detail.offerState
      });
    });
  }

  ctaListeners() {
    const $this = this;
    let formEl = this.getFormElement();
    // Track button clicks
    const buttons = formEl.querySelectorAll('button');
    buttons.forEach(function(button) {
      button.addEventListener('click', function() {
        $this.dataLayerEvent('formCTAs', {
          category: $this.formName + ' - ' + 'Form CTAs',
          action: $this.formName + ' - ' + button.innerText,
          label: button.innerText,
        });
      });
    });
    // Track dialog / more info clicks
    const infoLinks = formEl.querySelectorAll('.mdc-dialog__trigger');
    infoLinks.forEach(function(infoLink) {
      infoLink.addEventListener('click', function() {
        let name = $this.getFieldNameOrLabelText(this);
        $this.dataLayerEvent('formCTAs', {
          category: $this.formName + ' - ' + 'Form CTAs',
          action: $this.formName + ' - ' + name + ' info',
          label: name + ' info',
        });
      });
    });
  }

  getFieldNameOrLabelText(field) {
    let name = '';
    const selectParent = field.closest('.mdc-select');
    if (field.name) {
      name = field.name;
    }
    else if (selectParent) {
      const input = selectParent.querySelector('input');
      name = input.name;
    }
    else {
      const label = field.parentElement.querySelector('label');
      name = label.innerText;
    }
    return name;
  }

  raqFormListener() {
    const $this = this;
    window.addEventListener('requestQuote', function(e) {
      $this.dataLayerEvent('requestquote', {
        category: 'Quote Requested',
        action: 'RAQ Form | Buyer: ' + e.detail.buyerHash + ', Offer: ' + e.detail.offerHash + ', ProductCode: ' + e.detail.productCode,
      });
    });
  }

  // Helper to get a dataLayer event structure
  // Pass event name, and then data as an object
  // Common defaults will be assumed for action, category, label in data if none are supplied
  dataLayerEvent(eventName, data) {
    data.event = eventName;
    const defaultData = {
      label: this.pagePath,
      category: this.formName
    }
    for (var key in defaultData) {
      if (!data[key]) {
        data[key] = defaultData[key];
      }
    }
    this.dataLayer.push(data);
  }
}

if (window.document.readyState == 'interactive' || window.document.readyState == 'complete') {
  initEoiFormAnalytics();
}
else {
  window.addEventListener("DOMContentLoaded", initEoiFormAnalytics);
}

function initEoiFormAnalytics() {
  new EOIFormAnalytics(window.document, window.Livewire);
}
